import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { QrCode, Printer } from "lucide-react";
import { toast } from "sonner";

export function QRCodeModal({ open, onClose, qrCodeData }) {
  if (!qrCodeData) return null;

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = qrCodeData.qr_code;
    link.download = `QR_${qrCodeData.machine_number}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("QR code downloaded");
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Asset Tag - ${qrCodeData.machine_number}</title>
          <style>
            @page { size: 4in 2in; margin: 0.25in; }
            body { font-family: Arial, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; padding: 20px; }
            .asset-tag { border: 2px solid #000; padding: 15px; text-align: center; background: white; }
            .machine-number { font-size: 24px; font-weight: bold; margin-bottom: 10px; font-family: monospace; }
            .machine-name { font-size: 14px; margin-bottom: 10px; }
            .identifiers { font-size: 10px; margin-bottom: 15px; font-family: monospace; }
            img { max-width: 200px; height: auto; }
          </style>
        </head>
        <body>
          <div class="asset-tag">
            <div class="machine-number">${qrCodeData.machine_number}</div>
            <div class="machine-name">${qrCodeData.machine.name}</div>
            <div class="identifiers">
              ${qrCodeData.machine.serial_number ? `SN: ${qrCodeData.machine.serial_number}<br>` : ""}
              ${qrCodeData.machine.asset_number ? `Asset: ${qrCodeData.machine.asset_number}<br>` : ""}
            </div>
            <img src="${qrCodeData.qr_code}" alt="QR Code" />
          </div>
          <script>window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; };</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" aria-describedby="qr-code-description">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-[#0F172A]">Asset Tag QR Code</DialogTitle>
        </DialogHeader>
        <div id="qr-code-description" className="sr-only">Generate and print QR code for asset tag</div>
        <div className="space-y-4">
          <div className="bg-[#FAFAFA] border border-[#E2E8F0] p-6 text-center">
            <div className="mb-4">
              <div className="font-mono text-2xl font-bold text-[#2563EB] mb-2">{qrCodeData.machine_number}</div>
              <div className="text-sm text-[#0F172A] mb-2">{qrCodeData.machine.name}</div>
              {qrCodeData.machine.serial_number && (
                <div className="text-xs text-[#64748B] font-mono">SN: {qrCodeData.machine.serial_number}</div>
              )}
              {qrCodeData.machine.asset_number && (
                <div className="text-xs text-[#64748B] font-mono">Asset: {qrCodeData.machine.asset_number}</div>
              )}
            </div>
            <div className="flex justify-center">
              <img src={qrCodeData.qr_code} alt="QR Code" className="w-64 h-64 border-2 border-[#E2E8F0]" />
            </div>
          </div>
          <div className="text-xs text-[#64748B] bg-[#FAFAFA] p-3 border border-[#E2E8F0]">
            <strong className="text-[#0F172A]">QR Code Contains:</strong>
            <ul className="mt-1 ml-4 list-disc">
              <li>Machine ID</li>
              <li>Machine Number</li>
              <li>Serial Number</li>
              <li>Asset Number</li>
              <li>Machine Name</li>
            </ul>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleDownload} className="flex-1 bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="download-qr-button">
              <QrCode size={16} className="mr-2" /> Download PNG
            </Button>
            <Button onClick={handlePrint} variant="outline" className="flex-1 rounded-none border-[#E2E8F0]" data-testid="print-qr-button">
              <Printer size={16} className="mr-2" /> Print Tag
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
